/**
 * WO#2 — TerritoryCapacity (READ-ONLY surface).
 *
 * Compact territory-capacity dossier embedded inside the existing Parcel
 * Profile. Reuses the current chamfer/spec visual language. Shows ONLY
 * authoritative derived values: Territory Area, Publishability, GP Capacity,
 * Property Units. NEVER shows Used GP / Available GP / any content state
 * (there is no authorized content system yet). When the complete territory
 * cannot be authoritatively resolved, a clear non-authoritative unavailable
 * state is shown instead of a fabricated number.
 */
import {
  useTerritoryResolution,
  type SelectedParcelRef,
} from "./useTerritoryResolution";
import { useI18n } from "../i18n";
import { MIN_PUBLISHABLE_AREA } from "../domain/grid-power";

export function TerritoryCapacity({
  parcel,
}: {
  parcel: SelectedParcelRef | null;
}) {
  const { t } = useI18n();
  const r = useTerritoryResolution(parcel);
  if (!parcel || r.status === "idle") return null;

  return (
    <div data-testid="territory-capacity" className="flex flex-col gap-3">
      <div
        aria-hidden
        className="h-px w-full"
        style={{ background: "var(--spec-hairline)" }}
      />
      <span className="font-mono text-[8.5px] uppercase tracking-[0.32em] text-text-muted">
        {t("grid.capacity.territory")}
      </span>

      {r.status === "loading" && (
        <span
          data-testid="tc-loading"
          className="font-mono text-[11px] text-text-muted"
        >
          {t("grid.capacity.resolving")}
        </span>
      )}

      {r.status === "unavailable" && (
        <span
          data-testid="tc-unavailable"
          className="font-mono text-[11px] text-text-muted leading-relaxed"
        >
          {t("grid.capacity.unavailable")}
        </span>
      )}

      {r.status === "resolved" && (
        <div className="flex flex-col gap-3">
          <Row label={t("grid.capacity.area")}>
            <span
              data-testid="tc-area"
              className="font-mono text-[12px] text-text-primary tabular-nums"
            >
              {r.area.toLocaleString()} u²
            </span>
          </Row>

          <Row label={t("grid.capacity.publishability")}>
            {r.publishable ? (
              <span
                data-testid="tc-publishability"
                data-publishable="true"
                className="font-mono text-[11px] uppercase tracking-[0.18em]"
                style={{ color: "var(--spec-cyan)" }}
              >
                {t("grid.capacity.publishable")}
              </span>
            ) : (
              <span
                data-testid="tc-publishability"
                data-publishable="false"
                className="font-mono text-[11px] leading-relaxed text-text-muted"
              >
                {t("grid.capacity.notPublishable", { min: MIN_PUBLISHABLE_AREA.toLocaleString() })}
              </span>
            )}
          </Row>

          <div className="grid grid-cols-2 gap-3">
            <Row label={t("grid.capacity.gpCapacity")}>
              <span
                data-testid="tc-gp"
                className="font-mono text-[12px] tabular-nums"
                style={{ color: "var(--spec-cyan)" }}
              >
                {r.capacity} GP
              </span>
            </Row>
            <Row label={t("grid.capacity.propertyUnits")}>
              <span
                data-testid="tc-units"
                className="font-mono text-[12px] text-text-primary tabular-nums"
              >
                {r.unitCount}
              </span>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[8.5px] uppercase tracking-[0.32em] text-text-muted">
        {label}
      </span>
      {children}
    </div>
  );
}
